import React, { useState, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export function MaintenanceTools({ authToken }) {
  const [compressing, setCompressing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${authToken}` } }),
    [authToken]
  );

  const handleCompressPhotos = async () => {
    if (!window.confirm(
      "Möchten Sie alle Profilbilder komprimieren?\n\n" +
      "Dies reduziert den Speicherverbrauch um ca. 50% bei gleichbleibender Bildqualität.\n\n" +
      "Der Vorgang kann einige Sekunden dauern."
    )) {
      return;
    }

    setCompressing(true);
    setResult(null);
    setError(null);

    try {
      const response = await axios.post(
        `${API_BASE}/api/children/migrate/compress-photos`,
        {},
        authHeaders()
      );
      setResult(response.data);
    } catch (err) {
      console.error("Fehler bei der Foto-Komprimierung:", err);
      setError(err.response?.data?.detail || "Ein Fehler ist aufgetreten");
    }

    setCompressing(false);
  };

  return (
    <div className="rounded-xl bg-white shadow-sm overflow-hidden" data-testid="maintenance-tools">
      <div className="bg-gradient-to-r from-slate-50 to-white px-4 py-3 border-b border-slate-100">
        <h3 className="text-base font-bold text-slate-900">Wartung & Optimierung</h3>
        <p className="text-xs text-slate-500">Tools zur Systemoptimierung und Datenbankpflege</p>
      </div>

      <div className="p-4 space-y-6">
        {/* Foto-Komprimierung */}
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900">Profilbilder komprimieren</h4>
              <p className="mt-1 text-xs text-slate-500">
                Komprimiert alle vorhandenen Profilbilder auf 60% JPEG-Qualität. 
                Dies spart ca. 50% Speicherplatz bei gleichbleibender visueller Qualität (300×300 Pixel).
              </p>
              <p className="mt-2 text-xs text-slate-400">
                Empfohlen nach einem Redeployment oder wenn viele Fotos hochgeladen wurden.
              </p>
              
              <button
                onClick={handleCompressPhotos}
                disabled={compressing}
                data-testid="compress-photos-btn"
                className={`mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                  compressing
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-violet-600 text-white hover:bg-violet-700"
                }`}
              >
                {compressing ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Komprimierung läuft...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Fotos jetzt komprimieren
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Ergebnis-Anzeige */}
          {result && (
            <div className="mt-4 rounded-lg bg-green-50 p-4 border border-green-200" data-testid="compress-result">
              <div className="flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-green-800">{result.message}</p>
                  <ul className="mt-2 text-xs text-green-700 space-y-1">
                    <li>Gefunden: {result.details?.total_found || 0} Fotos</li>
                    <li>Komprimiert: {result.details?.compressed || 0} Fotos</li>
                    <li>Übersprungen: {result.details?.skipped || 0} Fotos</li>
                    <li className="font-medium">Gespart: {result.details?.savings_kb?.toFixed(1) || 0} KB</li>
                  </ul>
                  {result.details?.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-amber-700">
                      <p className="font-medium">Fehler bei {result.details.errors.length} Foto(s):</p>
                      <ul className="mt-1 list-disc list-inside">
                        {result.details.errors.map((e, i) => (
                          <li key={i}>{e.name}: {e.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Fehler-Anzeige */}
          {error && (
            <div className="mt-4 rounded-lg bg-red-50 p-4 border border-red-200" data-testid="compress-error">
              <div className="flex items-start gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Info-Box */}
        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
          <div className="flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-xs text-blue-800">
              <p className="font-medium">Hinweis zur Foto-Speicherung</p>
              <p className="mt-1">
                Neue Fotos werden automatisch mit optimierter Komprimierung (60% Qualität, 300×300 Pixel) gespeichert.
                Dieses Tool ist nur für die Migration älterer, noch nicht komprimierter Fotos notwendig.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
